/**
 * Login screen.
 *
 * Two sign-in paths, both of which the API drives:
 *   1. Email + password — posts to `POST /api/v2/auth/login`, which sets the
 *      session cookies and returns the new session. No redirect.
 *   2. "Sign in with Microsoft" — navigates the whole window to
 *      `GET /api/v2/auth/oauth/microsoft/start`. The API performs the code
 *      exchange itself and redirects back to this app's origin already signed
 *      in, so there is no callback route here and nothing to await.
 *
 * The page also surfaces `?error=<reason>` — set by the *API* when the OAuth
 * handshake fails, since it redirects to `<origin>/login?error=…` rather than
 * rendering a JSON body the user cannot navigate away from.
 */

import { ACCOUNT_DENIAL_MESSAGES, isApiError } from "@compliance/domain";
import {
  Alert,
  Box,
  Button,
  Center,
  Container,
  Divider,
  Group,
  Loader,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import omaiLogo from "../assets/omai-logo.png";
import { AccountInactive } from "../auth/AccountInactive";
import { clearMicrosoftResumeTarget } from "../auth/resumeMicrosoftSession";
import { usableAttributes } from "../auth/session";
import type { NewPasswordChallenge } from "../auth/types";
import { useAuth } from "../auth/useAuth";

/**
 * Turn a Cognito attribute name into a field label — `given_name` → "Given name".
 * Cognito's required-attribute names are snake_case identifiers, and showing them
 * raw in a form a new user meets on their first sign-in reads as a bug.
 */
function attributeLabel(name: string): string {
  const words = name.replace(/^custom:/, "").split("_");
  const [first = "", ...rest] = words;
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(" ");
}

/**
 * Microsoft's four-square logo, per their identity branding guidelines: these four
 * colours in this arrangement, undistorted, and never recoloured to match a theme.
 *
 * Replaces `IconBrandWindows`, which drew the *Windows* mark - a different product
 * and a different logo from the one that identifies a Microsoft account.
 *
 * `aria-hidden` because the button's text already names the provider; announcing
 * the mark as well would read it twice.
 */
function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

/**
 * Map the API's OAuth failure reasons to user-facing copy. These are the
 * literal `?error=` values `browser_auth/router.py::_login_error` redirects
 * with. Unknown codes fall through to the raw value so a useful diagnostic is
 * never swallowed.
 */
export const OAUTH_ERROR_COPY: Record<string, string> = {
  // The one that is not the user's fault, and the commonest failure at
  // onboarding: their tenant is not registered for their organization. Rendering
  // it generically sends them to retry a sign-in that cannot succeed until an
  // administrator acts. Same condition as a TENANT_NOT_REGISTERED 403 arriving
  // mid-session, which `ACCOUNT_DENIAL_MESSAGES` answers with the same wording.
  // The same sentence the mid-session refusal shows, by reference rather than by
  // copy: the comment above asserted they match, and nothing enforced it.
  oauth_tenant: ACCOUNT_DENIAL_MESSAGES.TENANT_NOT_REGISTERED,
  return_target: "This app isn't an allowed sign-in destination for the API.",
  oauth: "Sign-in was cancelled or the identity provider returned an error.",
  oauth_state:
    "Sign-in expired or was opened in a new tab. Start the sign-in flow again from this tab.",
  oauth_exchange: "Couldn't complete the sign-in handshake. Try again.",
  // The same condition as an `IDENTITY_NOT_PROVISIONED` 403 arriving mid-session, so
  // the same sentence by reference rather than by copy — for the reason directly above.
  // The hand-written string had already drifted and lost the only actionable half of
  // it, leaving the redirect path telling the user nothing they could act on.
  oauth_identity: ACCOUNT_DENIAL_MESSAGES.IDENTITY_NOT_PROVISIONED,
  oauth_unavailable: "Sign-in is temporarily unavailable. Try again in a moment.",
};

/**
 * Translate the auth error (from `useAuth().error`) into clear, human copy.
 *
 * The common case: a session expires, `/me` 401s, `AuthProvider` clears the
 * session and bounces here with the raw `ApiError`. Showing "401" /
 * "UNAUTHENTICATED" is confusing — surface "your session expired" instead. A
 * backend-down bounce (network/timeout) gets its own message; anything else
 * keeps its raw message so we don't swallow a useful diagnostic.
 */
const SESSION_EXPIRED_CODES = new Set(["UNAUTHENTICATED", "TOKEN_EXPIRED"]);

export function authErrorCopy(err: Error): {
  title: string;
  message: string;
  color: string;
} {
  if (isApiError(err)) {
    if (err.httpStatus === 401 || SESSION_EXPIRED_CODES.has(err.code)) {
      return {
        title: "Session expired",
        message: "Your session has expired. Please sign in again.",
        color: "blue",
      };
    }
    if (err.code === "NETWORK_ERROR" || err.code === "TIMEOUT") {
      return {
        title: "Can't reach the server",
        message: "We couldn't reach the server. Check your connection and try again.",
        color: "yellow",
      };
    }
  }
  return { title: "Sign-in failed", message: err.message, color: "red" };
}

/**
 * Has the API declared the challenge unusable, as opposed to rejecting this
 * particular password? A spent challenge cannot be retried; a weak password can.
 */
function isSpentChallenge(err: unknown): boolean {
  return isApiError(err) && (err.code === "AUTH_CHALLENGE" || err.httpStatus === 401);
}

export function LoginPage() {
  const {
    user,
    session,
    loading,
    signIn,
    completeNewPassword,
    signInWithMicrosoft,
    error,
    accountDenial,
    identityContractError,
    identityUnhandledError,
    sessionUnavailable,
  } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);
  // Non-null once the API answers login with NEW_PASSWORD_REQUIRED. Held in
  // component state only — `challengeSession` is short-lived provider state that
  // must not be persisted, so a reload correctly restarts the sign-in.
  const [challenge, setChallenge] = useState<NewPasswordChallenge | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [attributes, setAttributes] = useState<Record<string, string>>({});

  // Reaching the login form means the resume did not complete, so the route it
  // stashed is no longer part of a round trip and must not be replayed for whoever
  // signs in next. Left otherwise, an abandoned resume - the handshake failing and
  // landing back here with `?error=` - would send the *next* person to sign in at
  // this browser to the previous user's route.
  //
  // Here rather than in `clearMicrosoftResumeAttempt`, which runs the moment `/me`
  // succeeds: that is exactly when `RequireAuth` still needs the target, so
  // clearing it there would race the restore it exists for.
  useEffect(() => {
    clearMicrosoftResumeTarget();
  }, []);

  // OAuth-side errors surface here via the API's redirect to /login?error=…
  const oauthErrorCode = searchParams.get("error");
  const oauthErrorMessage = oauthErrorCode
    ? (OAUTH_ERROR_COPY[oauthErrorCode] ?? `Sign-in error: ${oauthErrorCode}`)
    : null;

  const clearOauthError = () => {
    if (!searchParams.has("error")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("error");
    setSearchParams(next, { replace: true });
  };

  // Session-expiry / BE-down bounces land here with a raw ApiError — map it to
  // clear copy ("Session expired", etc.) instead of showing the 401/400 string.
  const authError = error ? authErrorCopy(error) : null;

  // A restored-but-denied session (e.g. landing on /login via a bookmarked URL
  // or after an IdP logout redirect) must show the account-denial screen with a
  // deliberate sign-out — not the infinite in-flight loader below (user is null
  // for a denial, so the `session && !user` branch would otherwise spin forever).
  if (accountDenial) {
    return <AccountInactive code={accountDenial.code} />;
  }

  // Same reasoning as account denial, for a /me response that failed schema
  // validation (a version skew) instead of an access decision — it's
  // deterministic, so the loader below would spin forever.
  if (identityContractError) {
    return <AccountInactive code="CONTRACT_VIOLATION" />;
  }

  // Any other deterministic failure (e.g. an unexpected 404/422) — same
  // reasoning as above.
  if (identityUnhandledError) {
    return <AccountInactive code="UNKNOWN" />;
  }

  // A session that could not be RENEWED is not a session that ended. Offering
  // the credentials form here would be misleading twice over: the user's password
  // is not the problem, and signing in would fail against the same outage.
  if (sessionUnavailable) {
    return (
      <Center h="100vh" p="md">
        <Stack align="center" gap="md" maw={420}>
          <Alert
            color="yellow"
            variant="light"
            icon={<IconAlertCircle size={16} />}
            title="Can't reach the server"
            data-test="session-unavailable"
          >
            {sessionUnavailable === "renewal"
              ? // We know a session exists: /session reported `refreshable`, which
                // means a refresh cookie was presented. Only the renewal failed.
                "You are still signed in, but we couldn't renew your session. This is usually temporary."
              : // /session itself never answered, so nothing is known about a
                // session — claiming the visitor is signed in would assert what we
                // never learned, and a first-time visitor would simply be confused.
                "We couldn't determine whether you're signed in. This is usually temporary."}
          </Alert>
          <Button onClick={() => window.location.reload()}>Try again</Button>
        </Stack>
      </Center>
    );
  }

  // While the session bootstrap is in flight, don't flicker the login UI. We
  // also wait here while /me is in flight (session set, user null) so the form
  // doesn't reappear after a successful sign-in.
  if (loading || (session && !user)) {
    return (
      <Center h="100vh">
        <Loader size="md" />
      </Center>
    );
  }

  // Already signed in → bounce to the original destination (or /batches).
  if (user) {
    const from = (location.state as { from?: string } | null)?.from ?? "/batches";
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setPending(true);
    setSignInError(null);
    // The OAuth reason is in the query string, so it outlives the attempt that
    // produced it. Left in place it masks this form's own errors — a wrong
    // password would silently keep showing "sign-in was cancelled".
    clearOauthError();
    try {
      const outcome = await signIn(email, password);
      // A first sign-in must replace its temporary password before any session
      // exists. Switch the form rather than navigating — nothing is signed in.
      if (outcome.kind === "challenge") {
        setChallenge(outcome.challenge);
        setPassword("");
      }
      // Otherwise no navigate() here: `user` becomes non-null once /me resolves
      // and the redirect above fires. Navigating now would race that fetch.
    } catch (err) {
      setSignInError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  };

  const onSubmitNewPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!challenge) return;
    if (newPassword !== confirmPassword) {
      setSignInError("The two passwords do not match.");
      return;
    }
    setPending(true);
    setSignInError(null);
    clearOauthError();
    try {
      await completeNewPassword(challenge, newPassword, attributes);
      // Session established. The `user` redirect above takes over once /me
      // resolves; drop the challenge so a re-render can't resubmit stale state.
      setChallenge(null);
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setSignInError(err instanceof Error ? err.message : String(err));
      // A challenge is short-lived provider state. Once the API says it is spent,
      // re-submitting only repeats the failure, and leaving it set hides both the
      // sign-in form and the Microsoft button — so the only way out would be a
      // reload nothing tells the user to perform. Drop it and let them start over
      // with the error still on screen.
      if (isSpentChallenge(err)) {
        setChallenge(null);
        setNewPassword("");
        setConfirmPassword("");
        setAttributes({});
      }
    } finally {
      setPending(false);
    }
  };

  const alert = oauthErrorMessage
    ? { color: "red", title: "Sign-in failed", message: oauthErrorMessage, code: oauthErrorCode }
    : signInError
      ? { color: "red", title: "Sign-in failed", message: signInError, code: null }
      : authError
        ? {
            color: authError.color,
            title: authError.title,
            message: authError.message,
            code: null,
          }
        : null;

  return (
    <Box
      style={(t) => ({
        minHeight: "100vh",
        background: `linear-gradient(135deg, light-dark(${t.colors.indigo[0]}, ${t.colors.dark[8]}) 0%, light-dark(${t.white}, ${t.colors.dark[7]}) 60%, light-dark(${t.colors.cyan[0]}, ${t.colors.dark[6]}) 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: t.spacing.lg,
      })}
    >
      <Container size={460}>
        <Stack align="center" gap="md" mb="xl">
          <img src={omaiLogo} alt="OMai" style={{ height: 40, width: "auto" }} />
          <Stack gap={2} align="center">
            <Title order={2} fw={600} lh={1.1}>
              Compliance Platform
            </Title>
            <Text size="sm" c="dimmed" lh={1}>
              RFS · LCFS reporting for RNG producers
            </Text>
          </Stack>
        </Stack>

        <Paper p="xl" radius="lg" shadow="sm" withBorder>
          <Stack gap="lg">
            <Stack gap={4}>
              <Title order={3} fw={600}>
                {challenge ? "Choose a password" : "Sign in"}
              </Title>
              {challenge ? (
                <Text size="sm" c="dimmed">
                  Your account was created with a temporary password. Choose a permanent one to
                  finish signing in.
                </Text>
              ) : null}
            </Stack>

            {alert ? (
              <Alert
                color={alert.color}
                variant="light"
                icon={<IconAlertCircle size={16} />}
                title={alert.title}
              >
                <Stack gap={4}>
                  <Text size="sm">{alert.message}</Text>
                  {alert.code ? (
                    <Text size="xs" c="dimmed" ff="monospace">
                      {alert.code}
                    </Text>
                  ) : null}
                </Stack>
              </Alert>
            ) : null}

            {challenge ? (
              <form onSubmit={onSubmitNewPassword}>
                <Stack gap="md">
                  <PasswordInput
                    label="New password"
                    autoComplete="new-password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.currentTarget.value)}
                    data-test="new-password"
                  />
                  <PasswordInput
                    label="Confirm new password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.currentTarget.value)}
                    data-test="confirm-password"
                  />
                  {/* Cognito may demand attributes alongside the password. `email` and
                      `email_verified` are filtered out: the API refuses them because the
                      identity resolver selects the account by them. */}
                  {usableAttributes(challenge.requiredAttributes).map((name) => (
                    <TextInput
                      key={name}
                      label={attributeLabel(name)}
                      required
                      value={attributes[name] ?? ""}
                      // Read the value before the updater closure: React has
                      // nulled `currentTarget` by the time a lazy updater runs.
                      onChange={(e) => {
                        const value = e.currentTarget.value;
                        setAttributes((prev) => ({ ...prev, [name]: value }));
                      }}
                      data-test={`required-attribute-${name}`}
                    />
                  ))}
                  <Button type="submit" size="md" loading={pending} data-test="new-password-submit">
                    Set password and sign in
                  </Button>
                </Stack>
              </form>
            ) : (
              <form onSubmit={onSubmit}>
                <Stack gap="md">
                  <TextInput
                    label="Email"
                    type="email"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.currentTarget.value)}
                    data-test="login-email"
                  />
                  <PasswordInput
                    label="Password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.currentTarget.value)}
                    data-test="login-password"
                  />
                  <Button type="submit" size="md" loading={pending} data-test="login-submit">
                    Sign in
                  </Button>
                </Stack>
              </form>
            )}

            {!challenge ? (
              <>
                <Divider
                  label={
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.06em">
                      or
                    </Text>
                  }
                  labelPosition="center"
                />
                <Group justify="center">
                  <Button
                    size="md"
                    variant="default"
                    fullWidth
                    leftSection={<MicrosoftLogo />}
                    onClick={signInWithMicrosoft}
                    disabled={pending}
                    data-test="login-microsoft"
                  >
                    Sign in with Microsoft
                  </Button>
                </Group>
              </>
            ) : null}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
